import React, { useMemo, useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { StepProps } from "./StepComponents";
import {
  DEFAULT_EVENT_REMINDER_MODES,
  eventReminderLabel,
} from "../../constants/eventReminderOptions";
import EventReminderModesField from "./EventReminderModesField";
import { composerPanelBorderSx } from "./composerBorderStyles";

function buildReminderSummary(modes: string[]): string {
  if (modes.length === 0) {
    return "No reminders";
  }
  return modes.map(eventReminderLabel).filter(Boolean).join(", ");
}

const EventReminderSettingCard: React.FC<StepProps> = ({
  formData,
  handleChange,
}) => {
  const theme = useTheme();

  const reminderModes = useMemo(
    () =>
      formData.eventReminderModes?.length
        ? formData.eventReminderModes
        : formData.eventReminderMode && formData.eventReminderMode !== "off"
          ? [formData.eventReminderMode]
          : DEFAULT_EVENT_REMINDER_MODES,
    [formData.eventReminderModes, formData.eventReminderMode],
  );

  const collapsedSummary = useMemo(
    () => buildReminderSummary(reminderModes),
    [reminderModes],
  );

  // Start expanded so reminder timing is visible without an extra click/scroll miss.
  const [expanded, setExpanded] = useState(true);

  const updateReminderModes = (modes: string[]) => {
    handleChange({
      target: { name: "eventReminderModes", value: modes },
    });
    handleChange({
      target: {
        name: "eventReminderMode",
        value: modes[0] || "off",
      },
    });
  };

  return (
    <Box
      sx={{
        ...composerPanelBorderSx,
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((open) => !open);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <NotificationsOutlinedIcon fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          <Typography variant="subtitle1" fontWeight={400} lineHeight={1.3}>
            Reminder
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, lineHeight: 1.35 }}
          >
            {expanded
              ? "Choose when to send push reminders before the event."
              : collapsedSummary}
          </Typography>
        </Box>

        <ExpandMoreIcon
          sx={{
            flexShrink: 0,
            color: "text.secondary",
            transition: "transform 0.2s ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box sx={{ pt: 1.5 }}>
            <EventReminderModesField
              value={reminderModes}
              onChange={updateReminderModes}
              embedded
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default EventReminderSettingCard;
